import { HttpClient } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import { ISession, SessionModel } from '../models/session.model';
import { IServerResponse, calculateTotalPages } from '../models/server-response.model';
import { SpinnerService } from './spinner.service';
import { ToastService } from './toast.service';
import { UtilService } from './util.service';

export interface ISessionsFilters {
  uidUser?: string;
  active?: boolean;
  page?: number;
  pageSize?: number;
}

@Injectable({ providedIn: 'root' })
export class SessionService {
  private readonly http = inject(HttpClient);
  private readonly util = inject(UtilService);
  private readonly spinner = inject(SpinnerService);
  private readonly toast = inject(ToastService);
  private readonly urlApi = `${environment.urlApi}/sessions`;

  readonly loading = signal(false);
  readonly sessions = signal<SessionModel[]>([]);
  readonly total = signal(0);
  readonly page = signal(1);
  readonly pageSize = signal(20);
  readonly totalPages = computed(() => calculateTotalPages(this.total(), this.pageSize()));

  /**
   * `false` mientras NO se sabe todavia si hay datos: la primera peticion no ha
   * terminado. La vista pinta esqueletos hasta que pase a `true`; sin esta
   * senal el primer render enseñaria el estado vacio con la lista aun cargando.
   */
  readonly sessionsLoaded = signal(false);

  /** Mensaje del ultimo fallo de carga, o `null` si la carga salio bien. */
  readonly sessionsError = signal<string | null>(null);

  async getSessions(filters: ISessionsFilters = {}): Promise<SessionModel[]> {
    this.loading.set(true);
    try {
      const params = this.util.buildParams({
        uid_user: filters.uidUser,
        active: filters.active,
        page: filters.page,
        page_size: filters.pageSize,
      });
      const response = await firstValueFrom(
        this.http.get<
          IServerResponse<{ sessions: ISession[]; total: number; page: number; pageSize: number }>
        >(this.urlApi, { params }),
      );

      if (response.statusCode !== 200) {
        this.sessions.set([]);
        this.total.set(0);
        this.sessionsError.set(response.message);
        this.toast.error(response.message);
        return [];
      }

      const sessions = (response.data?.sessions ?? []).map((session) => new SessionModel(session));
      this.sessions.set(sessions);
      this.total.set(response.data?.total ?? 0);
      this.page.set(response.data?.page ?? 1);
      this.pageSize.set(response.data?.pageSize ?? 20);
      this.sessionsError.set(null);
      return sessions;
    } catch (error) {
      this.sessions.set([]);
      this.total.set(0);
      const message = this.util.errorMessage(error, 'No fue posible cargar las sesiones.');
      this.sessionsError.set(message);
      this.toast.error(message);
      return [];
    } finally {
      this.loading.set(false);
      this.sessionsLoaded.set(true);
    }
  }

  /**
   * PUT /sessions/{sessionId}/close - sin body.
   *
   * Si la sesion ya estaba cerrada el API responde 400 con el mensaje
   * "La sesion ya esta cerrada" DENTRO del envelope: se lee el cuerpo y se
   * avisa por toast, no se propaga la excepcion.
   */
  async closeSession(sessionId: string): Promise<SessionModel | null> {
    this.spinner.show('Cerrando sesión...');
    try {
      const response = await firstValueFrom(
        this.http.put<IServerResponse<{ session: ISession }>>(
          `${this.urlApi}/${sessionId}/close`,
          {},
        ),
      );

      if (response.statusCode !== 200) {
        this.toast.error(response.message);
        return null;
      }

      this.toast.success(response.message);
      return response.data?.session ? new SessionModel(response.data.session) : null;
    } catch (error) {
      this.toast.error(this.util.errorMessage(error, 'No fue posible cerrar la sesión.'));
      return null;
    } finally {
      this.spinner.hide();
    }
  }
}
